import { useState, useEffect, useRef } from "react";
import { useLoading } from "../../../../../../hooks/useLoading";
import { IDnsEntriesResponse } from "../../../../../../utils/fetch";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import { DomainService } from "../../../../../../services/domainService";
import { getDnsDelegateDomain } from "../../../../../../utils/dnsDelegateApi";

export type DnsEntriesMode = "admin" | "delegate";

interface UseDnsEntriesProps {
  mode?: DnsEntriesMode;
  charityId?: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  adminUrl?: string;
  delegateToken?: string;
}

function dnsEntriesScopeKey(
  mode: DnsEntriesMode,
  charityId?: string,
  delegateToken?: string
): string {
  return `${mode}:${charityId ?? ""}:${delegateToken ?? ""}`;
}

export const useDnsEntries = ({
  mode = "admin",
  charityId,
  config,
  serviceAccessToken,
  adminUrl,
  delegateToken,
}: UseDnsEntriesProps) => {
  const [domainData, setDomainData] = useState<IDnsEntriesResponse | null>(null);
  const [isDomainValid, setIsDomainValid] = useState<boolean>(false);
  const { loading: isFetchingDns, runWithLoading, setLoading } = useLoading(true);
  const [error, setError] = useState<Error | null>(null);
  const loadedScopeRef = useRef<string | null>(null);

  const resolvedAdminUrl = adminUrl || config?.adminServiceUrl;
  const scopeKey = dnsEntriesScopeKey(mode, charityId, delegateToken);

  useEffect(() => {
    if (loadedScopeRef.current === scopeKey) {
      setLoading(false);
      return;
    }

    if (mode === "admin" && !charityId) {
      setLoading(false);
      return;
    }

    if (mode === "admin" && (!resolvedAdminUrl || !serviceAccessToken)) {
      setLoading(false);
      return;
    }

    if (mode === "delegate" && (!resolvedAdminUrl || !delegateToken)) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    runWithLoading(async () => {
      try {
        let response: IDnsEntriesResponse;
        if (mode === "delegate" && resolvedAdminUrl && delegateToken) {
          response = (await getDnsDelegateDomain(
            resolvedAdminUrl,
            delegateToken
          )) as IDnsEntriesResponse;
        } else {
          response = await DomainService.getDomainData(
            config,
            charityId,
            serviceAccessToken
          );
        }
        if (cancelled) {
          return;
        }
        loadedScopeRef.current = scopeKey;
        setDomainData(response);
        setIsDomainValid(response.valid);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("Failed to fetch DNS entries"));
        }
      }
    }, 600);

    return () => {
      cancelled = true;
    };
  }, [
    scopeKey,
    mode,
    charityId,
    resolvedAdminUrl,
    serviceAccessToken,
    delegateToken,
    runWithLoading,
    setLoading,
  ]);

  return {
    domainData,
    isFetchingDns,
    isDomainValid,
    error,
  };
};
